import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, updateData, deleteData, activateData, postData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Typography,
  Grid,
  Box,
  Snackbar,
  Alert,
  Chip,
  TextField,
  MenuItem,
  InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Collapse,
  useMediaQuery
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  FilterList,
  Refresh,
  Assignment,
  Edit as EditIcon,
  Warning,
  CheckCircle,
  Add,
  Search,
  ExpandMore,
  ExpandLess
} from '@mui/icons-material';
import PurchaseReturnModal from './PurchaseReturnModal';
import Pagination from '../../common/Pagination';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import MampillyLogo from '../Logo/logo_Madavana.png';

const PurchaseReturnAgainstIndex = () => {
  // Session validation
  useSessionValidation();
  
  const { token, companyId, branchId, userId } = useAuth();
  const { logAudit } = useAuditLog();

  // State management
  const [purchaseReturns, setPurchaseReturns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [returnDetails, setReturnDetails] = useState([]);
  const [editFormData, setEditFormData] = useState({});
  const [gstType, setGstType] = useState(1); // 1 = Excluding Tax, 2 = Including Tax
  const [editReturnDetails, setEditReturnDetails] = useState([]);

  // Cancel confirmation modal for Edit
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalEditData, setOriginalEditData] = useState(null);
  const [originalEditDetails, setOriginalEditDetails] = useState([]);

  // Delete confirmation modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemInfo, setDeleteItemInfo] = useState({ billNumber: '', supplier: '' });

  // Activate confirmation modal
  const [activateConfirmOpen, setActivateConfirmOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemInfo, setActivateItemInfo] = useState({ billNumber: '', supplier: '' });

  // Create Purchase Return modal
  const [createModalOpen, setCreateModalOpen] = useState(false);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch Suppliers
  const fetchSuppliers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      const supplierData = response.response_data || response || [];
      setSuppliers(Array.isArray(supplierData) ? supplierData : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  // Fetch Purchase Returns (Active and Inactive)
  const fetchPurchaseReturns = async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchase returns...');
    try {
      // Fetch Active Purchase Returns
      const activeResponse = await fetchData('PurchaseReturnApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeRawData = activeResponse.response_data || activeResponse || [];
      const activeData = (Array.isArray(activeRawData) ? activeRawData : []).map(item => ({
        ...item,
        IsActive: item.IsActive ?? true
      }));

      // // Fetch Inactive Purchase Returns
      // let inactiveData = [];
      // try {
      //   const inactiveResponse = await fetchData('PurchaseReturnApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      //   const inactiveRawData = inactiveResponse.response_data || inactiveResponse || [];
      //   inactiveData = (Array.isArray(inactiveRawData) ? inactiveRawData : []).map(item => ({
      //     ...item,
      //     IsActive: false
      //   }));
      // } catch (inactiveError) {
      //   console.error('Error fetching inactive purchase returns:', inactiveError);
      // }

      setPurchaseReturns([...activeData]);
    } catch (error) {
      console.error('Error fetching purchase returns:', error);
      setError('Failed to fetch purchase returns');
      setPurchaseReturns([]);
    }
    setLoading(false);
  };

  // Fetch inactive Purchase Return details for view modal
  const fetchInactivePurchaseReturnDetails = async (id) => {
    try {
      const response = await fetchData('PurchaseReturnApi', `/InActivePurchaseReturnListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase return details:', error);
      return null;
    }
  };

  useEffect(() => {
    if (companyId && token && fromDate && toDate) {
      fetchSuppliers();
      fetchPurchaseReturns();
    }
  }, [companyId, token, fromDate, toDate]);

  // Track changes in edit form
  useEffect(() => {
    if (editModalOpen && originalEditData) {
      const formChanged = JSON.stringify(editFormData) !== JSON.stringify(originalEditData);
      const detailsChanged = JSON.stringify(editReturnDetails) !== JSON.stringify(originalEditDetails);
      setHasUnsavedChanges(formChanged || detailsChanged);
    }
  }, [editFormData, editReturnDetails, originalEditData, originalEditDetails, editModalOpen]);

  // Handle View
  const handleView = async (purchaseReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      let response;
      // Use different API endpoint for inactive items
      if (purchaseReturn.IsActive === false) {
        response = await fetchInactivePurchaseReturnDetails(purchaseReturn.Id);
      } else {
        response = await fetchData('PurchaseReturnApi', `/${purchaseReturn.Id}`, token);
        response = response.response_data || response;
      }

      if (!response) {
        setError('Failed to fetch purchase return details');
        setLoading(false);
        return;
      }

      setSelectedReturn(response.PurchaseReturn || response);
      setReturnDetails(response.PurchaseReturnDetails || []);
      setViewModalOpen(true);
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      setError('Failed to fetch purchase return details');
    }
    setLoading(false);
  };

  // Handle Edit
  const handleEdit = async (purchaseReturn) => {
    setLoading(true);
    setLoadingMessage('Loading details...');
    try {
      // const response = await fetchData('PurchaseReturnApi', `/PurchaseReturn/${purchaseReturn.Id}/${branchId}`, token);
      const response = await fetchData('PurchaseReturnApi', `/${purchaseReturn.Id}`, token);
      const data = response.response_data || response;
      const returnData = data.PurchaseReturn || data;
      const details = data.PurchaseReturnDetails || [];

      setSelectedReturn(returnData);
      setReturnDetails(details);

      // Initialize edit return details with return quantities
      const initialEditDetails = details.map(detail => ({
        ...detail,
        ReturnQuantity: detail.Quantity || 0,
        // Use PurchaseQuantity from API response
        PurchasedQuantity: detail.PurchaseQuantity || detail.Quantity || 0,
        // Store original values for calculations
        OriginalRate: detail.Rate || 0,
        OriginalGSTRate: detail.GSTRate || 0
      }));
      setEditReturnDetails(initialEditDetails);

      setGstType(returnData.GstCalculationType || 1);

      const formData = {
        BillNumber: returnData.BillNumber || '',
        BillDate: returnData.BillDate?.split('T')[0] || '',
        PurchaseNo: returnData.PurchaseNo || '',
        MasterName: returnData.MasterName || '',
        Remarks: returnData.Remarks || '',
        InvoiceTypeName: returnData.InvoiceTypeName || '',
        GstCalculationType: returnData.GstCalculationType || 1,
        Amount: returnData.Amount || 0,
        DiscountAmount: returnData.DiscountAmount || 0,
        TaxableAmount: returnData.TaxableAmount || 0,
        CGSTAmount: returnData.CGSTAmount || 0,
        SGSTAmount: returnData.SGSTAmount || 0,
        IGSTAmount: returnData.IGSTAmount || 0,
        TotalAmount: returnData.TotalAmount || 0,
        FreightAmount: returnData.FreightAmount || 0,
        FreightGSTAmount: returnData.FreightGSTAmount || 0,
        FreightGSTRate: returnData.FreightGSTRate || 18,
        PackingAmount: returnData.PackingAmount || 0,
        PackingGSTAmount: returnData.PackingGSTAmount || 0,
        PackingGSTRate: returnData.PackingGSTRate || 18,
        TCSAmount: returnData.TCSAmount || 0,
        PaidAmount: returnData.PaidAmount || 0,
        RoundOff: returnData.RoundOff || 0
      };

      setEditFormData(formData);

      // Store original data for change detection
      setOriginalEditData(JSON.parse(JSON.stringify(formData)));
      setOriginalEditDetails(JSON.parse(JSON.stringify(initialEditDetails)));
      setHasUnsavedChanges(false);

      // Calculate GST rates based on items after a short delay to ensure state is set
      setTimeout(() => {
        // Calculate the effective GST rates
        const maxItemGSTRate = initialEditDetails.length > 0
          ? Math.max(...initialEditDetails.map(d => parseFloat(d.GSTRate) || 0))
          : 0;
        const effectiveFreightGSTRate = Math.max(18, maxItemGSTRate);
        const effectivePackingGSTRate = Math.max(18, maxItemGSTRate);

        // Update freight and packing GST amounts with correct rates
        const freightAmount = parseFloat(formData.FreightAmount) || 0;
        const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;
        const packingAmount = parseFloat(formData.PackingAmount) || 0;
        const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

        setEditFormData(prev => ({
          ...prev,
          FreightGSTRate: effectiveFreightGSTRate,
          FreightGSTAmount: freightGSTAmount,
          PackingGSTRate: effectivePackingGSTRate,
          PackingGSTAmount: packingGSTAmount
        }));
      }, 100);

      setEditModalOpen(true);
    } catch (error) {
      console.error('Error fetching purchase return details:', error);
      setError('Failed to fetch purchase return details');
    }
    setLoading(false);
  };

  // Check if invoice type is IGST
  // B to B and B to C use CGST+SGST, others use IGST
  const isIGSTInvoice = () => {
    const invoiceType = (editFormData.InvoiceTypeName || '').toLowerCase();
    // If invoice type contains 'b to b' or 'b to c' or 'b2b' or 'b2c', use CGST+SGST
    if (invoiceType.includes('b to b') || invoiceType.includes('b to c') ||
      invoiceType.includes('b2b') || invoiceType.includes('b2c')) {
      return false; // Use CGST + SGST
    }
    // For IGST type or any other type, use IGST
    return invoiceType.includes('igst') || invoiceType.length > 0;
  };

  // Check if tax is including
  const isIncludingTax = () => gstType === 2;

  // Handle return quantity change
  const handleReturnQuantityChange = (index, value) => {
    console.log('Return Qty Change:', { index, value, editReturnDetails: editReturnDetails[index] });

    // Allow empty string for user to clear the field
    if (value === '') {
      const updatedDetails = [...editReturnDetails];
      updatedDetails[index] = {
        ...updatedDetails[index],
        ReturnQuantity: 0,
        Quantity: 0,
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        TotalAmount: 0
      };
      setEditReturnDetails(updatedDetails);
      calculateReturnTotals(updatedDetails);
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    // Validate quantity
    if (numValue < 0) {
      setError('Return quantity cannot be negative');
      return;
    }
    if (numValue > detail.PurchasedQuantity) {
      setError(`Return quantity cannot exceed purchased quantity (${detail.PurchasedQuantity})`);
      return;
    }

    const returnQty = numValue;
    const rate = parseFloat(detail.Rate) || 0;
    const discountPercent = parseFloat(detail.DiscountPercentage) || 0;
    const gstRate = parseFloat(detail.GSTRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, gstAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      // Rate includes GST - calculate backwards
      const totalWithGst = returnQty * rate;
      baseAmount = totalWithGst / (1 + gstRate / 100);
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      gstAmount = (taxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      };
    } else {
      // Rate excludes GST - normal calculation
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * discountPercent) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      gstAmount = (taxableAmount * gstRate) / 100;

      updatedDetails[index] = {
        ...detail,
        ReturnQuantity: returnQty,
        Quantity: returnQty,
        Amount: amount,
        DiscountAmount: discountAmount,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      };
    }

    console.log('Updated Detail:', updatedDetails[index]);
    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount percentage change
  const handleDiscountPercentChange = (index, value) => {
    const numValue = parseFloat(value) || 0;
    if (numValue < 0 || numValue > 100) {
      setError('Discount percentage must be between 0 and 100');
      return;
    }

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const gstRate = parseFloat(detail.GSTRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, gstAmount, amount, discountAmount, baseAmount;

    if (isIncludingTax()) {
      const totalWithGst = returnQty * rate;
      baseAmount = totalWithGst / (1 + gstRate / 100);
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      gstAmount = (taxableAmount * gstRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      discountAmount = (baseAmount * numValue) / 100;
      amount = baseAmount;
      taxableAmount = baseAmount - discountAmount;
      gstAmount = (taxableAmount * gstRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: numValue,
      Amount: amount,
      DiscountAmount: discountAmount,
      TaxableAmount: taxableAmount,
      CGSTAmount: useIGST ? 0 : gstAmount / 2,
      SGSTAmount: useIGST ? 0 : gstAmount / 2,
      IGSTAmount: useIGST ? gstAmount : 0,
      TotalAmount: taxableAmount + gstAmount
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle discount amount change
  const handleDiscountAmountChange = (index, value) => {
    const numValue = parseFloat(value) || 0;

    const updatedDetails = [...editReturnDetails];
    const detail = updatedDetails[index];

    const returnQty = parseFloat(detail.ReturnQuantity) || 0;
    const rate = parseFloat(detail.Rate) || 0;
    const gstRate = parseFloat(detail.GSTRate) || 0;
    const useIGST = isIGSTInvoice();

    let taxableAmount, gstAmount, amount, discountPercent, baseAmount;

    if (isIncludingTax()) {
      const totalWithGst = returnQty * rate;
      baseAmount = totalWithGst / (1 + gstRate / 100);
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      gstAmount = (taxableAmount * gstRate) / 100;
    } else {
      baseAmount = returnQty * rate;
      amount = baseAmount;
      discountPercent = baseAmount > 0 ? (numValue / baseAmount) * 100 : 0;
      taxableAmount = baseAmount - numValue;
      gstAmount = (taxableAmount * gstRate) / 100;
    }

    updatedDetails[index] = {
      ...detail,
      DiscountPercentage: Math.round(discountPercent * 100) / 100,
      DiscountAmount: numValue,
      Amount: amount,
      TaxableAmount: taxableAmount,
      CGSTAmount: useIGST ? 0 : gstAmount / 2,
      SGSTAmount: useIGST ? 0 : gstAmount / 2,
      IGSTAmount: useIGST ? gstAmount : 0,
      TotalAmount: taxableAmount + gstAmount
    };

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Calculate return totals
  const calculateReturnTotals = (details, additionalCharges = {}) => {
    const totals = details.reduce((acc, d) => ({
      Amount: acc.Amount + (parseFloat(d.Amount) || 0),
      DiscountAmount: acc.DiscountAmount + (parseFloat(d.DiscountAmount) || 0),
      TaxableAmount: acc.TaxableAmount + (parseFloat(d.TaxableAmount) || 0),
      CGSTAmount: acc.CGSTAmount + (parseFloat(d.CGSTAmount) || 0),
      SGSTAmount: acc.SGSTAmount + (parseFloat(d.SGSTAmount) || 0),
      IGSTAmount: acc.IGSTAmount + (parseFloat(d.IGSTAmount) || 0),
      TotalAmount: acc.TotalAmount + (parseFloat(d.TotalAmount) || 0)
    }), { Amount: 0, DiscountAmount: 0, TaxableAmount: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0, TotalAmount: 0 });

    // Calculate freight and packing GST rates
    // Use the maximum of default 18% OR the highest GST rate among items
    const defaultFreightGSTRate = 18;
    const defaultPackingGSTRate = 18;
    const maxItemGSTRate = details.length > 0 ? Math.max(...details.map(d => parseFloat(d.GSTRate) || 0)) : 0;

    const effectiveFreightGSTRate = Math.max(defaultFreightGSTRate, maxItemGSTRate);
    const effectivePackingGSTRate = Math.max(defaultPackingGSTRate, maxItemGSTRate);

    // Use passed values or current state values
    const freightAmount = additionalCharges.FreightAmount !== undefined
      ? parseFloat(additionalCharges.FreightAmount) || 0
      : parseFloat(editFormData.FreightAmount) || 0;
    const freightGSTAmount = (freightAmount * effectiveFreightGSTRate) / 100;

    const packingAmount = additionalCharges.PackingAmount !== undefined
      ? parseFloat(additionalCharges.PackingAmount) || 0
      : parseFloat(editFormData.PackingAmount) || 0;
    const packingGSTAmount = (packingAmount * effectivePackingGSTRate) / 100;

    const tcsAmount = additionalCharges.TCSAmount !== undefined
      ? parseFloat(additionalCharges.TCSAmount) || 0
      : parseFloat(editFormData.TCSAmount) || 0;

    const grandTotal = totals.TotalAmount + freightAmount + freightGSTAmount + packingAmount + packingGSTAmount + tcsAmount;
    const roundOff = parseFloat((Math.round(grandTotal) - grandTotal).toFixed(2));
    const totalAmount = Math.round(grandTotal);

    setEditFormData(prev => ({
      ...prev,
      ...totals,
      FreightAmount: freightAmount,
      FreightGSTRate: effectiveFreightGSTRate,
      FreightGSTAmount: freightGSTAmount,
      PackingAmount: packingAmount,
      PackingGSTRate: effectivePackingGSTRate,
      PackingGSTAmount: packingGSTAmount,
      TCSAmount: tcsAmount,
      RoundOff: roundOff,
      TotalAmount: totalAmount
    }));
  };

  // Handle GST Type change
  const handleGstTypeChange = (value) => {
    const intValue = parseInt(value);
    setGstType(intValue);
    setEditFormData(prev => ({ ...prev, GstCalculationType: intValue }));

    // Recalculate all items with new GST type
    const updatedDetails = editReturnDetails.map(detail => {
      const returnQty = parseFloat(detail.ReturnQuantity) || 0;
      const rate = parseFloat(detail.Rate) || 0;
      const gstRate = parseFloat(detail.GSTRate) || 0;
      const useIGST = isIGSTInvoice();

      let taxableAmount, gstAmount, amount;

      if (intValue === 2) {
        // Including Tax
        const totalWithGst = returnQty * rate;
        taxableAmount = totalWithGst / (1 + gstRate / 100);
        amount = taxableAmount;
        gstAmount = (taxableAmount * gstRate) / 100;
      } else {
        // Excluding Tax
        amount = returnQty * rate;
        taxableAmount = amount;
        gstAmount = (taxableAmount * gstRate) / 100;
      }

      return {
        ...detail,
        Amount: amount,
        TaxableAmount: taxableAmount,
        CGSTAmount: useIGST ? 0 : gstAmount / 2,
        SGSTAmount: useIGST ? 0 : gstAmount / 2,
        IGSTAmount: useIGST ? gstAmount : 0,
        TotalAmount: taxableAmount + gstAmount
      };
    });

    setEditReturnDetails(updatedDetails);
    calculateReturnTotals(updatedDetails);
  };

  // Handle additional charges change
  const handleAdditionalChargeChange = (field, value) => {
    const numValue = parseFloat(value) || 0;

    // Create updated charges object
    const updatedCharges = {
      FreightAmount: field === 'FreightAmount' ? numValue : editFormData.FreightAmount,
      PackingAmount: field === 'PackingAmount' ? numValue : editFormData.PackingAmount,
      TCSAmount: field === 'TCSAmount' ? numValue : editFormData.TCSAmount
    };

    // Recalculate with new values
    if (editReturnDetails.length > 0) {
      calculateReturnTotals(editReturnDetails, updatedCharges);
    } else {
      setEditFormData(prev => ({ ...prev, [field]: numValue }));
    }
  };

  // Handle Save Edit
  const handleSaveEdit = async () => {
    setLoading(true);
    setLoadingMessage('Saving changes...');
    try {
      // Prepare the update payload with PurchaseReturn and PurchaseReturnDetails
      const updatePayload = {
        PurchaseReturn: {
          ...selectedReturn,
          BillDate: editFormData.BillDate,
          Remarks: editFormData.Remarks,
          GstCalculationType: editFormData.GstCalculationType,
          Amount: editFormData.Amount,
          DiscountAmount: editFormData.DiscountAmount,
          TaxableAmount: editFormData.TaxableAmount,
          CGSTAmount: editFormData.CGSTAmount,
          SGSTAmount: editFormData.SGSTAmount,
          IGSTAmount: editFormData.IGSTAmount,
          TotalAmount: editFormData.TotalAmount,
          FreightAmount: editFormData.FreightAmount,
          FreightGSTAmount: editFormData.FreightGSTAmount,
          FreightGSTRate: editFormData.FreightGSTRate,
          PackingAmount: editFormData.PackingAmount,
          PackingGSTAmount: editFormData.PackingGSTAmount,
          PackingGSTRate: editFormData.PackingGSTRate,
          TCSAmount: editFormData.TCSAmount,
          PaidAmount: editFormData.PaidAmount,
          RoundOff: editFormData.RoundOff,
          BalanceAmount: editFormData.TotalAmount - editFormData.PaidAmount
        },
        PurchaseReturnDetails: editReturnDetails.map(detail => ({
          ...detail,
          Quantity: detail.ReturnQuantity || detail.Quantity,
          Amount: detail.Amount,
          TaxableAmount: detail.TaxableAmount,
          CGSTAmount: detail.CGSTAmount,
          SGSTAmount: detail.SGSTAmount,
          IGSTAmount: detail.IGSTAmount,
          TotalAmount: detail.TotalAmount
        }))
      };

      await updateData('PurchaseReturnApi', `${selectedReturn.Id}`, updatePayload, token);
      setSuccess('Purchase return updated successfully');
      logAudit(AUDIT_ACTION.UPDATE, 'PurchaseReturnApi', selectedReturn.Id, { ...selectedReturn }, { ...editFormData });
      setHasUnsavedChanges(false);
      setEditModalOpen(false);
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error updating purchase return:', error);
      setError('Failed to update purchase return');
    }
    setLoading(false);
  };

  // Handle Cancel Edit - Check for unsaved changes
  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      setCancelConfirmOpen(true);
    } else {
      setEditModalOpen(false);
      setHasUnsavedChanges(false);
      setOriginalEditData(null);
      setOriginalEditDetails([]);
    }
  };

  // Confirm cancel without saving
  const handleConfirmCancel = () => {
    setCancelConfirmOpen(false);
    setEditModalOpen(false);
    setHasUnsavedChanges(false);
    setOriginalEditData(null);
    setOriginalEditDetails([]);
  };

  // Close cancel confirmation and stay in edit mode
  const handleCloseCancelConfirm = () => {
    setCancelConfirmOpen(false);
  };

  // Handle Delete - Open confirmation modal
  const handleDelete = (purchaseReturn) => {
    setDeleteItemId(purchaseReturn.Id);
    setDeleteItemInfo({
      billNumber: purchaseReturn.BillNumber,
      supplier: purchaseReturn.MasterName
    });
    setDeleteConfirmOpen(true);
  };

  // Confirm Delete
  const handleConfirmDelete = async () => {
    if (!deleteItemId) return;

    setDeleteConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Deleting purchase return...');
    try {
      await deleteData('PurchaseReturnApi', deleteItemId, token);
      setSuccess('Purchase return deleted successfully');
    logAudit(AUDIT_ACTION.DELETE, 'PurchaseReturnApi', deleteItemId, { Id: deleteItemId }, null);
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error deleting purchase return:', error);
      setError('Failed to delete purchase return');
    }
    setLoading(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', supplier: '' });
  };

  // Close delete confirmation
  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setDeleteItemId(null);
    setDeleteItemInfo({ billNumber: '', supplier: '' });
  };

  // Handle Activate - Open confirmation modal
  const handleActivate = (purchaseReturn) => {
    setActivateItemId(purchaseReturn.Id);
    setActivateItemInfo({
      billNumber: purchaseReturn.BillNumber,
      supplier: purchaseReturn.MasterName
    });
    setActivateConfirmOpen(true);
  };

  // Confirm Activate
  const handleConfirmActivate = async () => {
    if (!activateItemId) return;

    setActivateConfirmOpen(false);
    setLoading(true);
    setLoadingMessage('Activating purchase return...');
    try {
      // Use PUT request to activate endpoint
      // await updateData('PurchaseReturnApi', `/${activateItemId}/activate`, {}, token);
      await activateData('PurchaseReturnApi', `${activateItemId}`, token);
      setSuccess('Purchase return activated successfully');
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error activating purchase return:', error);
      setError('Failed to activate purchase return');
    }
    setLoading(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', supplier: '' });
  };

  // Close activate confirmation
  const handleCloseActivateConfirm = () => {
    setActivateConfirmOpen(false);
    setActivateItemId(null);
    setActivateItemInfo({ billNumber: '', supplier: '' });
  };

  // Handle Open Create Modal
  const handleOpenCreateModal = () => {
    setCreateModalOpen(true);
  };

  // Handle Close Create Modal
  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
  };

  // Handle Save Purchase Return
  const handleSavePurchaseReturn = async (formData) => {
    console.log('Saving purchase return:', formData);

    // Validation
    const validationErrors = [];

    if (!formData.billNo?.trim()) {
      validationErrors.push('Return Number is required');
    }

    if (!formData.billDate) {
      validationErrors.push('Return Date is required');
    }

    // Check if there are items with return quantity
    const itemsWithReturnQty = formData.items.filter(item => (item.rcvdQty || 0) > 0);
    if (itemsWithReturnQty.length === 0) {
      validationErrors.push('At least one item must have return quantity greater than 0');
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join(', '));
      return;
    }

    setLoading(true);
    setLoadingMessage('Creating purchase return...');

    try {
      const gstCalculationType = formData.gstType === 'Including Tax' ? 2 : 1;

      const dataToSend = {
        PurchaseReturn: {
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseNo: formData.purchaseNo || '',
          PurchaseDate: formData.purchaseDate || null,
          BillNumber: formData.billNo || formData.BillNumber/formData.Billpred,
          BillDate: formData.billDate,
          MasterId: parseInt(formData.supplierId),
          MasterName: formData.supplier,
          InvoiceTypeId: 0,
          GSTNumber: formData.gstNo || '',
          GstCalculationType: gstCalculationType,
          GstCalculationTypeName: formData.gstType || 'Excluding Tax',
          Amount: parseFloat(formData.summary.subtotal) || 0,
          DiscountAmount: parseFloat(formData.summary.discount) || 0,
          TaxableAmount: parseFloat(formData.summary.taxable) || 0,
          CGSTAmount: parseFloat(formData.summary.cgst) || 0,
          SGSTAmount: parseFloat(formData.summary.sgst) || 0,
          IGSTAmount: parseFloat(formData.summary.igst) || 0,
          FreightAmount: parseFloat(formData.additionalCharges.freight) || 0,
          FreightGSTRate: parseFloat(formData.summary.effectiveGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.summary.freightGST) || 0,
          PackingAmount: parseFloat(formData.additionalCharges.packing) || 0,
          PackingGSTRate: parseFloat(formData.summary.effectiveGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.summary.packingGST) || 0,
          TCSAmount: parseFloat(formData.additionalCharges.tcsAmount) || 0,
          RoundOff: parseFloat(formData.summary.roundOff) || 0,
          TotalAmount: parseFloat(formData.summary.total) || 0,
          PaidAmount: parseFloat(formData.additionalCharges.paidAmount) || 0,
          BalanceAmount: parseFloat(formData.summary.total - formData.additionalCharges.paidAmount) || 0,
          Remarks: formData.additionalCharges.remarks || '',
          IsActive: true
        },
        PurchaseReturnDetails: itemsWithReturnQty.map(item => ({
          Id: 0,
          CompanyId: companyId,
          BranchId: branchId,
          PurchaseReturnId: 0,
          ItemId: parseInt(item.itemId),
          ItemName: item.item || '',
          BatchNumber: item.batchNumber || '',
          PurchaseId: parseInt(formData.purchaseId) || 0,
          PurchaseQuantity: parseFloat(item.purchasedQty) || 0,
          Quantity: parseFloat(item.rcvdQty) || 0,
          ReturnStockQuantity: parseFloat(item.rcvdQty) || 0,
          Rate: parseFloat(item.rate) || 0,
          Amount: parseFloat((item.rcvdQty || 0) * (item.rate || 0)) || 0,
          DiscountPercentage: parseFloat(item.discPercent) || 0,
          DiscountAmount: parseFloat(item.discAmt) || 0,
          HSNCode: item.hsnCode || '',
          GSTRate: parseFloat(item.gstPercent) || 0,
          TaxableAmount: parseFloat(item.taxable) || 0,
          CGSTAmount: parseFloat(item.cgst) || 0,
          SGSTAmount: parseFloat(item.sgst) || 0,
          IGSTAmount: parseFloat(item.igst) || 0,
          TotalAmount: parseFloat(item.total) || 0,
          IsActive: true
        }))
      };

      console.log('Data to send:', dataToSend);

      await postData('PurchaseReturnApi', '', dataToSend, token);

      setSuccess('Purchase return created successfully!');
      logAudit(AUDIT_ACTION.CREATE, 'PurchaseReturnApi', 0, null, { BillNumber: formData.BillNumber || '' });
      setCreateModalOpen(false);
      fetchPurchaseReturns();
    } catch (error) {
      console.error('Error creating purchase return:', error);
      setError('Failed to create purchase return.');
    }

    setLoading(false);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  // Number to words helper
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones  = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine'];
    const teens = ['Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
    const tens  = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
    const cvt = (n) => { let r=''; if(n>=100){r+=ones[Math.floor(n/100)]+' Hundred ';n%=100;} if(n>=20){r+=tens[Math.floor(n/10)]+' ';n%=10;}else if(n>=10){r+=teens[n-10]+' ';return r;} if(n>0)r+=ones[n]+' '; return r; };
    let result='',n=num;
    const cr=Math.floor(n/10000000);if(cr>0){result+=cvt(cr)+'Crore ';n%=10000000;}
    const lk=Math.floor(n/100000);if(lk>0){result+=cvt(lk)+'Lakh ';n%=100000;}
    const th=Math.floor(n/1000);if(th>0){result+=cvt(th)+'Thousand ';n%=1000;}
    if(n>0)result+=cvt(n);
    return result.trim();
  };

  // Print Purchase Return — same style as SaleIndex (no bank section)
  const handlePrintReturn = async (prId) => {
    setLoading(true);
    setLoadingMessage('Generating PDF...');
    try {
      const res = await fetchData('PurchaseReturnApi', `/${prId}`, token);
      const data = res.response_data || res;
      const pr = data.PurchaseReturn || data;
      const details = data.PurchaseReturnDetails || [];

      // Fetch items list for fallback item name lookup
      let itemsListForPrint = [];
      try {
        const itemsRes = await fetchData('ItemApi', `/${companyId}`, token);
        itemsListForPrint = itemsRes.response_data || itemsRes || [];
      } catch (e) { /* use empty */ }

      // Fetch company / branch info
      let companyData = {}, branchData = {};
      try {
        const [coRes, brRes] = await Promise.allSettled([
          fetchData('CompanyApi', `/CompanyDetails/${companyId}`, token),
          fetchData('BranchApi', `/BranchDetails/${branchId}`, token),
        ]);
        if (coRes.status === 'fulfilled') companyData = coRes.value?.response_data || coRes.value || {};
        if (brRes.status === 'fulfilled') branchData  = brRes.value?.response_data  || brRes.value  || {};
      } catch (e) { /* use defaults */ }

      const coName       = companyData?.Name || 'Company Name';
      const coBranchName = branchData?.BranchName || branchData?.Name || '';
      const coGST        = branchData?.GSTNumber || companyData?.GSTNumber || '';
      const coState      = branchData?.StateName || companyData?.StateName || 'Kerala';
      const coStateCode  = branchData?.StateCode || companyData?.StateCode || '32';
      const coAddr       = [branchData?.AddressLine1, branchData?.AddressLine2, branchData?.Location, branchData?.PinCode]
                            .filter(Boolean).join(', ') || companyData?.Address || '';
      const coTel1  = branchData?.ContactNumber1 || companyData?.ContactNumber || '';
      const coTel2  = branchData?.ContactNumber2 || companyData?.ContactNumber2 || '';
      const coPhones = [coTel1, coTel2].filter(Boolean).join(',');

      const black = [0, 0, 0];
      const white = [255, 255, 255];

      const pdf = new jsPDF('p', 'mm', 'a4');
      const PW = pdf.internal.pageSize.width;
      const PH = pdf.internal.pageSize.height;
      const M  = 5;
      const CW = PW - 2 * M;

      const drawText = (text, x, y, opts = {}) => {
        const { fs = 8, fw = 'normal', col = black, align = 'left', mw = null } = opts;
        pdf.setFontSize(fs); pdf.setFont('helvetica', fw); pdf.setTextColor(...col);
        const str = String(text ?? '');
        if (mw) { const lines = pdf.splitTextToSize(str, mw); pdf.text(lines, x, y, { align }); return y + lines.length * fs * 0.38; }
        pdf.text(str, x, y, { align }); return y + fs * 0.38;
      };
      const hLine = (x1, y1, x2, y2, lw = 0.3) => { pdf.setDrawColor(0,0,0); pdf.setLineWidth(lw); pdf.line(x1,y1,x2,y2); };
      const drawRect = (x, y, w, h, lw = 0.3) => { pdf.setDrawColor(0,0,0); pdf.setLineWidth(lw); pdf.rect(x,y,w,h,'S'); };

      let y = M;

      // ── OUTER BORDER ─────────────────────────────────────────────────────
      drawRect(M, M, CW, PH - 2 * M, 0.5);

      // ── HEADER ───────────────────────────────────────────────────────────
      const logoSize  = 14;
      const rightColX = M + CW * 0.68;
      const leftTextX = M + logoSize + 6;
      const leftMaxW  = rightColX - leftTextX - 4;
      const rightValX = rightColX + 22;
      const rightMaxW = M + CW - rightValX - 2;

      pdf.setFontSize(7);
      const addrLines = pdf.splitTextToSize(coAddr, leftMaxW);
      const headerH   = Math.max(26, 14 + addrLines.length * 3.5 + 8);

      try { pdf.addImage(MampillyLogo, 'PNG', M + 3, y + 3, logoSize, logoSize); } catch (e) {}

      drawText(coName.toUpperCase(), leftTextX, y + 7, { fs: 12, fw: 'bold' });
      let headerTextY = y + 12;
      pdf.setFontSize(7); pdf.setFont('helvetica','normal'); pdf.setTextColor(...black);
      pdf.text(addrLines, leftTextX, headerTextY);
      headerTextY += addrLines.length * 3.5;
      drawText(`GST NO : ${coGST}  State : ${coState} - ${coStateCode}`, leftTextX, headerTextY, { fs: 7 });
      headerTextY += 4;
      if (coPhones) drawText(coPhones, leftTextX, headerTextY, { fs: 7 });

      // const billNo   = [pr.BillPrefix, pr.BillNumber, pr.FinYear].filter(Boolean).join('/');
      const billNo   = [pr.BillNumber].filter(Boolean).join('/');
      const billDate = formatDate(pr.BillDate);

      hLine(rightColX, y, rightColX, y + headerH);
      let ry = y + 5;
      drawText('Return No',    rightColX+2, ry, { fs:7, fw:'bold' }); drawText(billNo,               rightValX, ry, { fs:7, mw:rightMaxW }); ry+=5;
      drawText('Date',         rightColX+2, ry, { fs:7, fw:'bold' }); drawText(billDate,              rightValX, ry, { fs:7, mw:rightMaxW }); ry+=5;
      drawText('Against Pur.', rightColX+2, ry, { fs:7, fw:'bold' }); drawText(pr.PurchaseNo || '-', rightValX, ry, { fs:7, mw:rightMaxW }); ry+=5;
      drawText('Branch',       rightColX+2, ry, { fs:7, fw:'bold' }); drawText(coBranchName || '-',  rightValX, ry, { fs:6.5, mw:rightMaxW });

      y += headerH;
      hLine(M, y, M + CW, y);

      // ── DEBIT NOTE TITLE ─────────────────────────────────────────────────
      drawText('DEBIT NOTE', M + CW / 2, y + 5, { fs: 9, fw: 'bold', align: 'center' });
      drawText('(PURCHASE RETURN)', M + CW / 2, y + 10, { fs: 8, fw: 'bold', align: 'center' });
      y += 14;
      hLine(M, y, M + CW, y);

      // ── SUPPLIER SECTION ─────────────────────────────────────────────────
      const supplierName    = pr.MasterName || pr.SupplierName || pr.VendorName || '-';
      const supplierGST     = pr.GSTNumber || '';
      const supplierPhone   = pr.ContactNumber || pr.Phone || '';
      const supplierAddress = pr.Address || pr.SupplierAddress || '';
      const suppState       = pr.StateName || coState;
      const suppStateCode   = pr.StateCode || coStateCode;

      const midX     = M + CW * 0.5;
      const custRowH = 22;
      drawText('To',           M+2, y+4,  { fs:7, fw:'bold' });
      drawText(supplierName,   M+2, y+8,  { fs:8, fw:'bold', mw: midX-M-5 });
      let custY = y + 13;
      if (supplierAddress) { drawText(supplierAddress, M+2, custY, { fs:7, col:[60,60,60], mw:midX-M-5 }); custY+=4; }
      if (supplierPhone)   { drawText(supplierPhone,   M+2, custY, { fs:7, col:[30,64,175], mw:midX-M-5 }); }

      hLine(midX, y, midX, y + custRowH);
      drawText('GST No :',      midX+2, y+4,  { fs:7, fw:'bold' }); drawText(supplierGST,                   midX+22, y+4,  { fs:7 });
      drawText('Invoice Type:', midX+2, y+10, { fs:7, fw:'bold' }); drawText(pr.InvoiceTypeName || '-',     midX+22, y+10, { fs:7 });

      y += custRowH;
      hLine(M, y, M + CW, y);

      // State row
      drawText(`State : ${suppState}-${suppStateCode}`, M+2, y+4, { fs:7 });
      if (pr.Remarks) drawText(`Remarks : ${pr.Remarks}`, M + CW * 0.35, y+4, { fs:7, mw: CW*0.6 });
      y += 6;
      hLine(M, y, M + CW, y);

      // ── ITEMS TABLE ──────────────────────────────────────────────────────
      const hasIGST = details.some(d => (d.IGSTAmount||0) > 0) || (pr.IGSTAmount||0) > 0;
      let tableHead, tableBody;

      if (hasIGST) {
        tableHead = [['No','Item','HSN/SAC Code','Rate','Dis %','S Rate','Ret Qty','IGST\n%','IGST\nAmt','Total']];
        tableBody = details.map((d, i) => {
          const qty   = Math.max(Number(d.Quantity||1), 1);
          const sRate = Number(d.TaxableAmount||0) / qty;
          const itemName = d.ItemName || (Array.isArray(itemsListForPrint) ? itemsListForPrint.find(it => it.Id === d.ItemId)?.Name : '') || '';
          return [i+1, itemName, d.HSNCode||'', Number(d.Rate||0).toFixed(2), Number(d.DiscountPercentage||0)>0?Number(d.DiscountPercentage).toFixed(2):'', sRate.toFixed(2), Number(d.Quantity||0).toFixed(2), d.GSTRate||'0', Number(d.IGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)];
        });
      } else {
        tableHead = [[
          {content:'No',rowSpan:2},{content:'Item',rowSpan:2},{content:'HSN/SAC Code',rowSpan:2},
          {content:'Rate',rowSpan:2},{content:'Dis %',rowSpan:2},{content:'S Rate',rowSpan:2},
          {content:'Ret Qty',rowSpan:2},{content:'CGST',colSpan:2},{content:'SGST',colSpan:2},
          {content:'Total',rowSpan:2},
        ],['%','Amt','%','Amt']];
        tableBody = details.map((d, i) => {
          const qty      = Math.max(Number(d.Quantity||1), 1);
          const sRate    = Number(d.TaxableAmount||0) / qty;
          const gstRate  = Number(d.GSTRate||0);
          const halfRate = gstRate / 2;
          const itemName = d.ItemName || (Array.isArray(itemsListForPrint) ? itemsListForPrint.find(it => it.Id === d.ItemId)?.Name : '') || '';
          return [i+1, itemName, d.HSNCode||'', Number(d.Rate||0).toFixed(2), Number(d.DiscountPercentage||0)>0?Number(d.DiscountPercentage).toFixed(2):'', sRate.toFixed(2), Number(d.Quantity||0).toFixed(2), halfRate>0?halfRate.toFixed(1):'0', Number(d.CGSTAmount||0).toFixed(2), halfRate>0?halfRate.toFixed(1):'0', Number(d.SGSTAmount||0).toFixed(2), Number(d.TotalAmount||0).toFixed(2)];
        });
      }

      autoTable(pdf, {
        startY: y, head: tableHead, body: tableBody, theme: 'grid',
        headStyles: { fillColor:white, textColor:black, fontSize:7, fontStyle:'bold', halign:'center', cellPadding:1.5, lineColor:black, lineWidth:0.3 },
        bodyStyles: { fontSize:7, textColor:black, cellPadding:1.5, lineColor:black, lineWidth:0.2 },
        columnStyles: hasIGST ? {
          0:{halign:'center',cellWidth:8},1:{halign:'left'},2:{halign:'center',cellWidth:22},
          3:{halign:'right',cellWidth:18},4:{halign:'center',cellWidth:12},5:{halign:'right',cellWidth:20},
          6:{halign:'right',cellWidth:12},7:{halign:'center',cellWidth:10},8:{halign:'right',cellWidth:18},9:{halign:'right',cellWidth:22},
        } : {
          0:{halign:'center',cellWidth:8},1:{halign:'left'},2:{halign:'center',cellWidth:22},
          3:{halign:'right',cellWidth:17},4:{halign:'center',cellWidth:12},5:{halign:'right',cellWidth:18},
          6:{halign:'right',cellWidth:12},7:{halign:'center',cellWidth:10},8:{halign:'right',cellWidth:16},
          9:{halign:'center',cellWidth:10},10:{halign:'right',cellWidth:16},11:{halign:'right',cellWidth:20},
        },
        margin: { left:M, right:M },
        tableLineColor: black, tableLineWidth: 0.3,
      });
      y = (pdf.lastAutoTable?.finalY || y + 30);

      // ── TOTALS ROW ───────────────────────────────────────────────────────
      const totalQty   = details.reduce((s,d) => s + Number(d.Quantity||0), 0);
      const totalCGST  = Number(pr.CGSTAmount||0);
      const totalSGST  = Number(pr.SGSTAmount||0);
      const totalIGST  = Number(pr.IGSTAmount||0);
      const grandTotal = Number(pr.TotalAmount||0);
      const taxableAmt = Number(pr.Amount||pr.TaxableAmount||0);

      const totalsRowH = 7;
      hLine(M, y, M+CW, y, 0.3);
      hLine(M, y+totalsRowH, M+CW, y+totalsRowH, 0.3);

      if (hasIGST) {
        const fw=[8,0,22,18,12,20,12,10,18,22]; fw[1]=CW-fw.reduce((s,w)=>s+w,0);
        let cx=M; const cp=fw.map(w=>{const x=cx;cx+=w;return{x,width:w};});
        drawText(totalQty.toFixed(0), cp[6].x+cp[6].width/2, y+5, {fs:7,fw:'bold',align:'center'});
        drawText(totalIGST.toFixed(2), cp[8].x+cp[8].width-1, y+5, {fs:7,fw:'bold',align:'right'});
        drawText(grandTotal.toFixed(2), cp[9].x+cp[9].width-1, y+5, {fs:7,fw:'bold',align:'right'});
      } else {
        const fw=[8,0,22,17,12,18,12,10,16,10,16,20]; fw[1]=CW-fw.reduce((s,w)=>s+w,0);
        let cx=M; const cp=fw.map(w=>{const x=cx;cx+=w;return{x,width:w};});
        drawText(totalQty.toFixed(0), cp[6].x+cp[6].width/2, y+5, {fs:7,fw:'bold',align:'center'});
        drawText(totalCGST.toFixed(2), cp[8].x+cp[8].width-1, y+5, {fs:7,fw:'bold',align:'right'});
        drawText(totalSGST.toFixed(2), cp[10].x+cp[10].width-1, y+5, {fs:7,fw:'bold',align:'right'});
        drawText(grandTotal.toFixed(2), cp[11].x+cp[11].width-1, y+5, {fs:7,fw:'bold',align:'right'});
      }
      y += totalsRowH;

      // ── SUMMARY (right side, no bank) ────────────────────────────────────
      const bottomY       = y;
      const summaryDivX   = M + CW * 0.55;
      const summaryLabelX = summaryDivX + 3;
      const valX2         = M + CW - 3;

      let sy = bottomY + 5;
      const sumLine = (lbl, val) => {
        drawText(lbl, summaryLabelX, sy, { fs:7, fw:'bold' });
        drawText(val, valX2, sy, { fs:7, align:'right' });
        sy += 5;
      };
      sumLine('Taxable Amount', taxableAmt.toFixed(2));
      if (hasIGST) { sumLine('IGST', totalIGST.toFixed(2)); }
      else { sumLine('CGST', totalCGST.toFixed(2)); sumLine('SGST', totalSGST.toFixed(2)); }
      if (Number(pr.DiscountAmount||0) > 0)  sumLine('Discount',  Number(pr.DiscountAmount).toFixed(2));
      if (Number(pr.FreightAmount||0)  > 0)  sumLine('Freight',   Number(pr.FreightAmount).toFixed(2));
      if (Number(pr.RoundOff||0)      !== 0) sumLine('Round Off', Number(pr.RoundOff).toFixed(2));

      hLine(summaryDivX, sy, M+CW, sy); sy += 1;
      drawText('Grand Total', summaryLabelX, sy+4, { fs:9, fw:'bold' });
      drawText(grandTotal.toFixed(2), valX2, sy+4, { fs:9, fw:'bold', align:'right' });

      const bottomSectionEndY = sy + 8;
      hLine(summaryDivX, bottomY, summaryDivX, bottomSectionEndY);
      hLine(M, bottomSectionEndY, M+CW, bottomSectionEndY);

      // ── AMOUNT IN WORDS ──────────────────────────────────────────────────
      const wordsY = bottomSectionEndY + 5;
      drawText(`Rupees ${numberToWords(Math.floor(grandTotal))} and ${Math.round((grandTotal%1)*100)} Paise Only`,
        M+3, wordsY, { fs:7, fw:'bold', mw: summaryDivX - M - 5 });

      // ── FOOTER ───────────────────────────────────────────────────────────
      const footerLineY = wordsY + 6;
      hLine(M, footerLineY, M+CW, footerLineY);
      const sigLineY = footerLineY + 22;
      hLine(summaryDivX, footerLineY, summaryDivX, sigLineY);
      if (pr.Remarks) drawText(pr.Remarks, M+3, footerLineY+5, { fs:7, mw: summaryDivX - M - 8 });
      drawText(`For  ${coName.toUpperCase()}`, summaryDivX+3, footerLineY+5,  { fs:8, fw:'bold' });
      drawText('Authorised Signatory',         summaryDivX+3, footerLineY+18, { fs:8, fw:'bold' });
      hLine(M, sigLineY, M+CW, sigLineY, 0.5);

      pdf.autoPrint();
      const blob = pdf.output('blob');
      const url  = URL.createObjectURL(blob);
      const win  = window.open(url, '_blank');
      if (win) { win.onload = () => { win.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); }; }
      else { const a = document.createElement('a'); a.href=url; a.download=`PurchaseReturn_${billNo}.pdf`; a.click(); URL.revokeObjectURL(url); }
      setSuccess(`Purchase Return ${billNo} ready for printing.`);
    } catch (err) {
      console.error(err);
      setError('Failed to generate print.');
    }
    setLoading(false);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN');
  };

  // Filter purchase returns
  const filteredReturns = Array.isArray(purchaseReturns) ? purchaseReturns.filter(pr => {
    // Search filter
    if (searchTerm && !pr.BillNumber?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.MasterName?.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !pr.PurchaseNo?.toLowerCase().includes(searchTerm.toLowerCase())) return false;

    // Date range filter
    if (fromDate && new Date(pr.BillDate) < new Date(fromDate)) return false;
    if (toDate && new Date(pr.BillDate) > new Date(toDate)) return false;

    // Payment Status filter
    if (filterStatus !== 'all') {
      if (filterStatus === 'paid' && (pr.BalanceAmount > 0 || pr.TotalAmount !== pr.PaidAmount)) return false;
      if (filterStatus === 'unpaid' && (pr.PaidAmount > 0 || pr.BalanceAmount === 0)) return false;
      if (filterStatus === 'partial' && (pr.PaidAmount === 0 || pr.BalanceAmount === 0)) return false;
    }

    // Active/Inactive filter
    if (filterActive !== 'all') {
      if (filterActive === 'active' && pr.IsActive !== true) return false;
      if (filterActive === 'inactive' && pr.IsActive !== false) return false;
    }

    return true;
  }) : [];

  // Pagination
  const totalPages = Math.ceil(filteredReturns.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = filteredReturns.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontWeight: '700', margin: 0, letterSpacing: '-0.3px', fontFamily: 'Times New Roman', textTransform: 'uppercase', color: 'black' }}>
            Purchase Return 
          </h4>
        </div>

        {/* Search, Filters and Create Button */}
        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => setToDate(e.target.value)} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="paid">Paid</option>
                <option value="unpaid">Unpaid</option>
                <option value="partial">Partial</option>
              </select>
              <select value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={fetchPurchaseReturns} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Purchase Return" arrow>
                <IconButton onClick={handleOpenCreateModal} disabled={loading} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                  <Add sx={{ fontSize: '18px' }} />
                </IconButton>
              </Tooltip>
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Bill No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Bill No, Supplier..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: '180px', '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <TextField size="small" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '140px' }} />
            <TextField size="small" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '140px' }} />
            <TextField select size="small" value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '130px' }}>
              <MenuItem value="all">All Status</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="unpaid">Unpaid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
            </TextField>
            <TextField select size="small" value={filterActive} onChange={(e) => { setFilterActive(e.target.value); setCurrentPage(1); }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '12px', height: '29px' }, width: '100px' }}>
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <Tooltip title="Refresh" arrow>
              <Button variant="outlined" onClick={fetchPurchaseReturns} disabled={loading} sx={{ borderRadius: '6px', minHeight: '29px', minWidth: '29px', padding: '4px 8px', fontSize: '12px', textTransform: 'none' }}>
                <Refresh sx={{ fontSize: '16px' }} />
              </Button>
            </Tooltip>
            <Tooltip title="Add Purchase Return" arrow>
              <Button variant="contained" onClick={handleOpenCreateModal} disabled={loading}
                sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Purchase Return</span>
              </Button>
            </Tooltip>
          </div>
        )}

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #2C3E6E 100%)', height: '27px' }}>
                  {['Sl No.', 'Return No', 'Date', 'Purchase No', 'Supplier', 'Total Amount', 'Status', 'Actions'].map((col, i) => (
                    <TableCell key={col} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 7 ? 'right' : 'left' }}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                      No purchase returns found
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedData.map((pr, index) => (
                    <TableRow key={pr.Id} style={{ background: pr.IsActive === false ? '#fee2e2' : (index % 2 === 0 ? '#ffffff' : '#f8f9fa'), borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#1e40af', fontWeight: 600 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Assignment sx={{ fontSize: 16, color: '#1e40af' }} />
                          {pr.BillNumber}
                        </div>
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(pr.BillDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{pr.PurchaseNo || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{pr.MasterName}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={formatCurrency(pr.TotalAmount)} size="small" sx={{ bgcolor: '#d1fae5', color: '#065f46', fontWeight: 600, fontSize: '11px', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={pr.IsActive ? 'Active' : 'Inactive'} size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: pr.IsActive ? '#d1fae5' : '#fee2e2', color: pr.IsActive ? '#065f46' : '#dc2626', fontWeight: 600 }} />
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <Button size="small" onClick={() => handleView(pr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>👁️</Button>
                          </Tooltip>
                          <Tooltip title="Print" arrow>
                            <Button size="small" onClick={() => handlePrintReturn(pr.Id)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🖨️</Button>
                          </Tooltip>
                          {pr.IsActive ? (
                            <>
                              <Tooltip title="Edit" arrow>
                                <Button size="small" onClick={() => handleEdit(pr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✏️</Button>
                              </Tooltip>
                              <Tooltip title="Delete" arrow>
                                <Button size="small" onClick={() => handleDelete(pr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>🗑️</Button>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title="Activate" arrow>
                              <Button size="small" onClick={() => handleActivate(pr)} style={{ minWidth: '22px', height: '22px', borderRadius: '6px', padding: '2px', fontSize: '10px' }}>✅</Button>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
          itemsPerPage={rowsPerPage}
          onItemsPerPageChange={(val) => { setRowsPerPage(Number(val)); setCurrentPage(1); }}
          totalItems={filteredReturns.length}
        />
      </div>

      <LogoLoader open={loading} message={loadingMessage} />

      <Snackbar open={!!error || !!success} autoHideDuration={6000} onClose={() => { setError(''); setSuccess(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={() => { setError(''); setSuccess(''); }} severity={error ? 'error' : 'success'}>{error || success}</Alert>
      </Snackbar>


      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: '#fafbfc', p: '6px 12px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.938rem' }}>
              Edit Purchase Return
            </Typography>
            <IconButton onClick={handleCancelEdit} size="small" sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' }, p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1, bgcolor: '#fafbfc' }}>
          {selectedReturn && (
            <>
              {/* Header Form */}
              <Box sx={{ bgcolor: '#fef3c7', borderRadius: 1.5, p: 1, mb: 1, border: '1px solid #fde68a' }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={6} sm={3} md={1.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.4px', mb: 0.2 }}>R.NO</Typography>
                    <TextField fullWidth size="small" value={editFormData.BillNumber || ''} disabled
                      sx={{ bgcolor: '#f9fafb', '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '0.75rem', fontWeight: 600 } }} />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.4px', mb: 0.2 }}>R.DATE</Typography>
                    <TextField fullWidth size="small" type="date" value={editFormData.BillDate || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, BillDate: e.target.value })}
                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '0.75rem' } }} />
                  </Grid>
                  <Grid item xs={12} sm={4} md={2.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.4px', mb: 0.2 }}>SUPPLIER</Typography>
                    <TextField fullWidth size="small" value={editFormData.MasterName || ''} disabled
                      sx={{ bgcolor: '#f9fafb', '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '0.75rem', fontWeight: 600 } }} />
                  </Grid>
                  <Grid item xs={6} sm={3} md={1.5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.4px', mb: 0.2 }}>GST TYPE</Typography>
                    <TextField fullWidth size="small" select value={gstType} onChange={(e) => handleGstTypeChange(e.target.value)}
                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '0.75rem' } }}>
                      <MenuItem value={1}>Excl. Tax</MenuItem>
                      <MenuItem value={2}>Incl. Tax</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6} md={5}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#92400e', display: 'block', textTransform: 'uppercase', fontSize: '0.625rem', letterSpacing: '0.4px', mb: 0.2 }}>REMARKS</Typography>
                    <TextField fullWidth size="small" placeholder="Enter remarks..." value={editFormData.Remarks || ''}
                      onChange={(e) => setEditFormData({ ...editFormData, Remarks: e.target.value })}
                      sx={{ bgcolor: 'white', '& .MuiOutlinedInput-input': { padding: '4px 8px', fontSize: '0.75rem' } }} />
                  </Grid>
                </Grid>
              </Box>

              {/* Return Items Table */}
              <Box sx={{ mb: 1 }}>
                <Box sx={{ bgcolor: 'white', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: '#fef3c7', borderBottom: '2px solid #fde68a' }}>
                          <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>#</th>
                          <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>ITEM</th>
                          <th style={{ padding: '4px 6px', textAlign: 'left', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>HSN</th>
                          {editFormData.PurchaseNo && (
                            <>
                              <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>PUR.QTY</th>
                              <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>BAL.QTY</th>
                            </>
                          )}
                          <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>RET.QTY</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>RATE</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>AMOUNT</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>DISC%</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>DISC AMT</th>
                          <th style={{ padding: '4px 6px', textAlign: 'center', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>GST%</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>TAXABLE</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>GST AMT</th>
                          <th style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 700, color: '#92400e', fontSize: '0.625rem', textTransform: 'uppercase' }}>TOTAL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editReturnDetails.map((item, index) => (
                          <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                            <td style={{ padding: '3px 6px', fontSize: '0.75rem', fontWeight: 500 }}>{index + 1}</td>
                            <td style={{ padding: '3px 6px', fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{item.ItemName}</td>
                            <td style={{ padding: '3px 6px', fontSize: '0.75rem', color: '#64748b' }}>{item.HSNCode || '-'}</td>
                            {editFormData.PurchaseNo && (
                              <>
                                <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600 }}>{item.PurchasedQuantity}</td>
                                <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600 }}>{item.BalanceQuantity}</td>
                              </>
                            )}
                            <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                              <TextField size="small" type="number" value={item.ReturnQuantity ?? ''}
                                onChange={(e) => handleReturnQuantityChange(index, e.target.value)}
                                inputProps={{ min: 0, max: item.BalanceQuantity, step: 1, style: { textAlign: 'center', fontSize: '0.75rem', padding: '3px 4px' } }}
                                sx={{ width: '60px', '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(item.Rate)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{formatCurrency(item.Amount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                              <TextField size="small" type="number" value={item.DiscountPercentage ?? ''}
                                onChange={(e) => handleDiscountPercentChange(index, e.target.value)}
                                inputProps={{ min: 0, max: 100, step: 0.01, style: { textAlign: 'right', fontSize: '0.75rem', padding: '3px 4px' } }}
                                sx={{ width: '60px', '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'right' }}>
                              <TextField size="small" type="number" value={item.DiscountAmount ?? ''}
                                onChange={(e) => handleDiscountAmountChange(index, e.target.value)}
                                inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', fontSize: '0.75rem', padding: '3px 4px' } }}
                                sx={{ width: '70px', '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                            </td>
                            <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>{item.GSTRate || 0}%</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{formatCurrency(item.TaxableAmount)}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{formatCurrency((item.CGSTAmount || 0) + (item.SGSTAmount || 0) + (item.IGSTAmount || 0))}</td>
                            <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{formatCurrency(item.TotalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </Box>

              {/* Summary Section */}
              <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Additional Charges */}
                <Box sx={{ bgcolor: 'white', borderRadius: 1.5, p: 1, border: '1px solid #e2e8f0', flex: '0 0 auto', width: { xs: '100%', md: '340px' } }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', display: 'block', mb: 0.5, fontSize: '0.688rem', textTransform: 'uppercase' }}>Additional Charges</Typography>
                  <Grid container spacing={0.75}>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', fontSize: '0.625rem', mb: 0.2 }}>FREIGHT <span style={{ color: '#059669' }}>({editFormData.FreightGSTRate || 18}% GST)</span></Typography>
                      <TextField fullWidth size="small" type="number" value={editFormData.FreightAmount || 0}
                        onChange={(e) => handleAdditionalChargeChange('FreightAmount', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 6px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', fontSize: '0.625rem', mb: 0.2 }}>PACKING <span style={{ color: '#059669' }}>({editFormData.PackingGSTRate || 18}% GST)</span></Typography>
                      <TextField fullWidth size="small" type="number" value={editFormData.PackingAmount || 0}
                        onChange={(e) => handleAdditionalChargeChange('PackingAmount', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 6px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', fontSize: '0.625rem', mb: 0.2 }}>TCS AMOUNT</Typography>
                      <TextField fullWidth size="small" type="number" value={editFormData.TCSAmount || 0}
                        onChange={(e) => handleAdditionalChargeChange('TCSAmount', e.target.value)}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 6px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', display: 'block', fontSize: '0.625rem', mb: 0.2 }}>PAID AMOUNT</Typography>
                      <TextField fullWidth size="small" type="number" value={editFormData.PaidAmount || 0}
                        onChange={(e) => setEditFormData({ ...editFormData, PaidAmount: parseFloat(e.target.value) || 0 })}
                        onFocus={(e) => e.target.select()}
                        inputProps={{ step: '0.01', style: { textAlign: 'right', fontSize: '0.75rem', padding: '4px 6px' } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 0.5 } }} />
                    </Grid>
                  </Grid>
                </Box>

                {/* Totals */}
                <Box sx={{ bgcolor: 'white', borderRadius: 1.5, p: 1, border: '1px solid #e2e8f0', flex: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#0f172a', display: 'block', mb: 0.5, fontSize: '0.688rem', textTransform: 'uppercase' }}>Summary</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                    {[
                      { label: 'Subtotal', value: editFormData.Amount, color: '#0f172a' },
                      { label: 'Discount', value: editFormData.DiscountAmount, color: '#dc2626' },
                      { label: 'Taxable', value: editFormData.TaxableAmount, color: '#0f172a' },
                      { label: 'GST', value: (editFormData.CGSTAmount || 0) + (editFormData.SGSTAmount || 0) + (editFormData.IGSTAmount || 0), color: '#dc2626' },
                      { label: 'Freight', value: editFormData.FreightAmount, color: '#0f172a' },
                      { label: 'Frt.GST', value: editFormData.FreightGSTAmount, color: '#dc2626' },
                      { label: 'Packing', value: editFormData.PackingAmount, color: '#0f172a' },
                      { label: 'Pkg.GST', value: editFormData.PackingGSTAmount, color: '#dc2626' },
                      { label: 'TCS', value: editFormData.TCSAmount, color: '#0f172a' },
                      { label: 'Round Off', value: editFormData.RoundOff, color: '#64748b', prefix: editFormData.RoundOff >= 0 ? '+' : '' },
                    ].map(({ label, value, color, prefix = '' }) => (
                      <Box key={label} sx={{ textAlign: 'center', px: 0.75, py: 0.4, bgcolor: '#f8fafc', borderRadius: 1, minWidth: 70 }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.563rem', fontWeight: 600, display: 'block' }}>{label}</Typography>
                        <Typography sx={{ fontWeight: 700, color, fontSize: '0.75rem' }}>{prefix}{formatCurrency(value)}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.75 }}>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 0.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1.5px solid #fde68a' }}>
                      <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.563rem', fontWeight: 700, display: 'block' }}>TOTAL</Typography>
                      <Typography sx={{ fontWeight: 800, color: '#ea580c', fontSize: '0.938rem' }}>{formatCurrency(editFormData.TotalAmount)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, textAlign: 'center', p: 0.5, bgcolor: '#dbeafe', borderRadius: 1, border: '1.5px solid #93c5fd' }}>
                      <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.563rem', fontWeight: 700, display: 'block' }}>BALANCE</Typography>
                      <Typography sx={{ fontWeight: 800, color: '#2563eb', fontSize: '0.938rem' }}>{formatCurrency((editFormData.TotalAmount || 0) - (editFormData.PaidAmount || 0))}</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

            </>
          )}
        </DialogContent>
        <Box sx={{ p: '6px 12px', borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button variant="outlined" onClick={handleCancelEdit}
            sx={{ textTransform: 'none', borderRadius: 1, px: 2, py: 0.5, fontWeight: 600, fontSize: '0.8rem' }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} startIcon={<EditIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none', borderRadius: 1, px: 2, py: 0.5, fontWeight: 600, fontSize: '0.8rem', background: 'linear-gradient(135deg, #10b981, #059669)', '&:hover': { background: 'linear-gradient(135deg, #059669, #047857)' } }}>
            Update
          </Button>
        </Box>
      </Dialog>

      {/* View Modal */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} maxWidth="xl" fullWidth
        PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid #e2e8f0', bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#fafbfc', p: '6px 12px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#0f172a', fontSize: '0.938rem' }}>
                View Purchase Return
              </Typography>
              {selectedReturn?.IsActive === false && (
                <Chip label="INACTIVE" size="small" sx={{ bgcolor: '#991b1b', color: 'white', fontWeight: 700, fontSize: '0.625rem', height: '18px' }} />
              )}
            </Box>
            <IconButton onClick={() => setViewModalOpen(false)} size="small"
              sx={{ bgcolor: selectedReturn?.IsActive === false ? '#fecaca' : '#f1f5f9', '&:hover': { bgcolor: selectedReturn?.IsActive === false ? '#fca5a5' : '#e2e8f0' }, p: 0.5 }}>
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 1, bgcolor: selectedReturn?.IsActive === false ? '#fef2f2' : '#fafbfc' }}>
          {selectedReturn && (
            <>
              {/* Header Info */}
              <Box sx={{ bgcolor: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderRadius: 1.5, p: 1, mb: 1, border: selectedReturn?.IsActive === false ? '1px solid #fca5a5' : '1px solid #93c5fd' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                  {[
                    { label: 'Return No', value: selectedReturn.BillNumber, bold: true },
                    { label: 'Date', value: formatDate(selectedReturn.BillDate) },
                    { label: 'Purchase No', value: selectedReturn.PurchaseNo || '-' },
                    { label: 'Supplier', value: selectedReturn.MasterName, bold: true },
                  ].map(({ label, value, bold }) => (
                    <Box key={label}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.563rem', letterSpacing: '0.4px' }}>{label}</Typography>
                      <Typography sx={{ fontWeight: bold ? 700 : 600, color: '#0f172a', fontSize: '0.813rem' }}>{value}</Typography>
                    </Box>
                  ))}
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.563rem', letterSpacing: '0.4px', mb: 0.3 }}>GST Type</Typography>
                    <Chip label={selectedReturn.GstCalculationType === 2 ? 'Incl. Tax' : 'Excl. Tax'} size="small"
                      sx={{ bgcolor: selectedReturn.GstCalculationType === 2 ? '#dcfce7' : '#dbeafe', color: selectedReturn.GstCalculationType === 2 ? '#166534' : '#1e40af', fontWeight: 600, fontSize: '0.625rem', height: '18px' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.563rem', letterSpacing: '0.4px', mb: 0.3 }}>Inv. Type</Typography>
                    <Chip label={selectedReturn.InvoiceTypeName || 'N/A'} size="small"
                      sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.625rem', height: '18px' }} />
                  </Box>
                  {selectedReturn.Remarks && (
                    <Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', display: 'block', textTransform: 'uppercase', fontSize: '0.563rem', letterSpacing: '0.4px' }}>Remarks</Typography>
                      <Typography sx={{ color: '#64748b', fontSize: '0.75rem', fontStyle: 'italic' }}>{selectedReturn.Remarks}</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Items Table */}
              <Box sx={{ mb: 1, bgcolor: 'white', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Box sx={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: selectedReturn?.IsActive === false ? '#fee2e2' : '#dbeafe', borderBottom: selectedReturn?.IsActive === false ? '2px solid #fca5a5' : '2px solid #93c5fd' }}>
                        {['#', 'ITEM', 'HSN', 'QTY', 'RATE', 'AMOUNT', 'DISC%', 'DISC AMT', 'GST%', 'TAXABLE', 'CGST', 'SGST', 'IGST', 'TOTAL'].map((h, i) => (
                          <th key={h} style={{ padding: '4px 6px', textAlign: i <= 2 ? 'left' : i === 3 || i === 8 ? 'center' : 'right', fontWeight: 700, color: selectedReturn?.IsActive === false ? '#991b1b' : '#1e40af', fontSize: '0.563rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {returnDetails.map((item, index) => (
                        <tr key={item.Id} style={{ borderBottom: '1px solid #f1f5f9', background: index % 2 === 0 ? '#fff' : '#fafafa' }}>
                          <td style={{ padding: '3px 6px', fontSize: '0.75rem', fontWeight: 500 }}>{index + 1}</td>
                          <td style={{ padding: '3px 6px', fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{item.ItemName}</td>
                          <td style={{ padding: '3px 6px', fontSize: '0.75rem', color: '#64748b' }}>{item.HSNCode || '-'}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 700, color: '#92400e' }}>{item.Quantity}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600 }}>{formatCurrency(item.Rate)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{formatCurrency(item.Amount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{item.DiscountPercentage || 0}%</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#dc2626', fontWeight: 600 }}>{formatCurrency(item.DiscountAmount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'center', fontSize: '0.75rem', color: '#059669', fontWeight: 600 }}>{item.GSTRate || 0}%</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#64748b' }}>{formatCurrency(item.TaxableAmount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#dc2626' }}>{formatCurrency(item.CGSTAmount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#dc2626' }}>{formatCurrency(item.SGSTAmount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', color: '#dc2626' }}>{formatCurrency(item.IGSTAmount)}</td>
                          <td style={{ padding: '3px 6px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 700, color: '#0f172a' }}>{formatCurrency(item.TotalAmount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Summary */}
              <Box sx={{ bgcolor: 'white', borderRadius: 1.5, p: 1, border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.75 }}>
                  {[
                    { label: 'Subtotal', value: selectedReturn.Amount, color: '#0f172a' },
                    { label: 'Discount', value: selectedReturn.DiscountAmount, color: '#dc2626' },
                    { label: 'Taxable', value: selectedReturn.TaxableAmount, color: '#0f172a' },
                    { label: 'CGST', value: selectedReturn.CGSTAmount, color: '#dc2626' },
                    { label: 'SGST', value: selectedReturn.SGSTAmount, color: '#dc2626' },
                    { label: 'IGST', value: selectedReturn.IGSTAmount, color: '#dc2626' },
                    { label: 'Freight', value: selectedReturn.FreightAmount, color: '#0f172a' },
                    { label: 'Frt.GST', value: selectedReturn.FreightGSTAmount, color: '#dc2626' },
                    { label: 'Packing', value: selectedReturn.PackingAmount, color: '#0f172a' },
                    { label: 'Pkg.GST', value: selectedReturn.PackingGSTAmount, color: '#dc2626' },
                    { label: 'TCS', value: selectedReturn.TCSAmount, color: '#0f172a' },
                    { label: 'Round Off', value: selectedReturn.RoundOff, color: '#64748b', prefix: selectedReturn.RoundOff >= 0 ? '+' : '' },
                  ].map(({ label, value, color, prefix = '' }) => (
                    <Box key={label} sx={{ textAlign: 'center', px: 0.75, py: 0.4, bgcolor: '#f8fafc', borderRadius: 1, minWidth: 68 }}>
                      <Typography variant="caption" sx={{ color: '#64748b', fontSize: '0.563rem', fontWeight: 600, display: 'block' }}>{label}</Typography>
                      <Typography sx={{ fontWeight: 700, color, fontSize: '0.75rem' }}>{prefix}{formatCurrency(value)}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', gap: 0.75 }}>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 0.5, bgcolor: '#dbeafe', borderRadius: 1, border: '1.5px solid #93c5fd' }}>
                    <Typography variant="caption" sx={{ color: '#1e40af', fontSize: '0.563rem', fontWeight: 700, display: 'block' }}>TOTAL</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#2563eb', fontSize: '0.938rem' }}>{formatCurrency(selectedReturn.TotalAmount)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 0.5, bgcolor: '#fef3c7', borderRadius: 1, border: '1.5px solid #fde68a' }}>
                    <Typography variant="caption" sx={{ color: '#92400e', fontSize: '0.563rem', fontWeight: 700, display: 'block' }}>PAID</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#ea580c', fontSize: '0.938rem' }}>{formatCurrency(selectedReturn.PaidAmount)}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, textAlign: 'center', p: 0.5, bgcolor: '#dcfce7', borderRadius: 1, border: '1.5px solid #86efac' }}>
                    <Typography variant="caption" sx={{ color: '#166534', fontSize: '0.563rem', fontWeight: 700, display: 'block' }}>BALANCE</Typography>
                    <Typography sx={{ fontWeight: 800, color: '#16a34a', fontSize: '0.938rem' }}>{formatCurrency(selectedReturn.BalanceAmount || (selectedReturn.TotalAmount - selectedReturn.PaidAmount))}</Typography>
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <Box sx={{ p: '6px 12px', borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => setViewModalOpen(false)}
            sx={{ textTransform: 'none', borderRadius: 1, px: 2, py: 0.5, fontWeight: 600, fontSize: '0.8rem', borderColor: '#3b82f6', color: '#3b82f6', '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' } }}>
            Close
          </Button>
        </Box>
      </Dialog>

      {/* Cancel Confirmation Modal */}
      <Dialog
        open={cancelConfirmOpen}
        onClose={handleCloseCancelConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef3c7',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#f59e0b', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#92400e', fontSize: '1.125rem' }}>
            Unsaved Changes
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6 }}>
            You have unsaved changes. Are you sure you want to cancel without saving? All changes will be lost.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseCancelConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Keep Editing
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmCancel}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#fef2f2',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <Warning sx={{ color: '#ef4444', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#991b1b', fontSize: '1.125rem' }}>
            Delete Purchase Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to delete this purchase return?
          </Typography>
          {deleteItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {deleteItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Supplier:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {deleteItemInfo.supplier}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#dc2626', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ⚠️ This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseDeleteConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDelete}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626, #b91c1c)'
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog
        open={activateConfirmOpen}
        onClose={handleCloseActivateConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
          }
        }}
      >
        <DialogTitle sx={{
          borderBottom: '1px solid #e2e8f0',
          bgcolor: '#d1fae5',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5
        }}>
          <CheckCircle sx={{ color: '#10b981', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#065f46', fontSize: '1.125rem' }}>
            Activate Purchase Return
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body1" sx={{ color: '#64748b', fontSize: '0.938rem', lineHeight: 1.6, mb: 2 }}>
            Are you sure you want to activate this purchase return?
          </Typography>
          {activateItemInfo.billNumber && (
            <Box sx={{
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              p: 2,
              border: '1px solid #e2e8f0'
            }}>
              <Grid container spacing={1}>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Return No:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 700, fontSize: '0.875rem' }}>
                    {activateItemInfo.billNumber}
                  </Typography>
                </Grid>
                <Grid item xs={5}>
                  <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, fontSize: '0.75rem' }}>
                    Supplier:
                  </Typography>
                </Grid>
                <Grid item xs={7}>
                  <Typography variant="body2" sx={{ color: '#0f172a', fontWeight: 600, fontSize: '0.875rem' }}>
                    {activateItemInfo.supplier}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
          <Typography variant="body2" sx={{ color: '#10b981', fontSize: '0.813rem', mt: 2, fontWeight: 600 }}>
            ✓ This will restore the purchase return to active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{
          p: 2,
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          gap: 1.5
        }}>
          <Button
            variant="outlined"
            onClick={handleCloseActivateConfirm}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              borderColor: '#64748b',
              color: '#64748b',
              '&:hover': {
                borderColor: '#475569',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmActivate}
            sx={{
              textTransform: 'none',
              borderRadius: 1.5,
              px: 2.5,
              py: 0.75,
              fontWeight: 600,
              fontSize: '0.875rem',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              '&:hover': {
                background: 'linear-gradient(135deg, #059669, #047857)'
              }
            }}
          >
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Purchase Return Modal */}
      <PurchaseReturnModal
        open={createModalOpen}
        onClose={handleCloseCreateModal}
        onSave={handleSavePurchaseReturn}
        companyId={companyId}
        branchId={branchId}
        token={token}
        suppliers={suppliers}
        setLoading={setLoading}
        setLoadingMessage={setLoadingMessage}
        setError={setError}
        setSuccess={setSuccess}
        fetchPurchaseReturns={fetchPurchaseReturns}
        setCreateModalOpen={setCreateModalOpen}
        userId={userId}
      />

    </div>
  );
};

export default PurchaseReturnAgainstIndex;

